import React from 'react';
import { useHistory } from 'react-router';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button, TextField, FormControlLabel, Switch, InputAdornment } from '@material-ui/core';
import { storeLuxuryBox, updateLuxuryBox } from 'services/venuesServices';
import SelectField from 'components/SelectField';
import ConfirmDialog from 'components/ConfirmDialog';
import LuxuryBoxFormProps from 'types/forms/LuxuryBoxFormProps';
import Commerce from 'types/models/Commerce';
import EditVenueContext from 'contexts/EditVenueContext';
import { CreateForm, EditForm } from './schemas/LuxuryBoxForm';
import LuxuryBox from 'types/models/LuxuryBox';
import 'styles/forms/LuxuryBoxForm.scss';

export default ({ luxuryBox }: LuxuryBoxFormProps): JSX.Element => {
  const { selectedVenue } = React.useContext(EditVenueContext);
  const history = useHistory();
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = React.useState(false);

  const commercesForSelect = (commerces: Commerce[]) => {
    return commerces.map((commerce) => {
      return {
        label: commerce.name,
        value: commerce.id,
      };
    });
  };

  const onCancel = (): void => {
    formik.setSubmitting(false);
  };

  const onConfirm = async (): Promise<void> => {
    if (!selectedVenue) return;
    try {
      let result: LuxuryBox | null = null;
      const payload = {
        floor: formik.values.floor,
        identifierOnFloor: formik.values.identifierOnFloor,
        usesCheckingAccount: formik.values.usesCheckingAccount,
        commerceIds: formik.values.commerces,
      };
      if (luxuryBox) {
        result = await updateLuxuryBox(selectedVenue.id, luxuryBox.id, payload);
      } else {
        result = await storeLuxuryBox(selectedVenue.id, payload);
      }
      if (result) {
        history.go(-1);
      }
    } catch {
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      floor: luxuryBox?.floor ?? 0,
      identifierOnFloor: luxuryBox?.identifierOnFloor ?? '',
      usesCheckingAccount: luxuryBox?.usesCheckingAccount ?? false,
      commerces: luxuryBox?.commerces?.map((commerce) => commerce.id) ?? [],
    },
    validationSchema: luxuryBox ? EditForm(t) : CreateForm(t),
    onSubmit: () => {
      setOpenDialog(true);
    },
  });

  return (
    <>
      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <form onSubmit={formik.handleSubmit}>
        <div className="floor-fields-container">
          <div className="floor-field">
            <TextField
              id="floor"
              label={t('luxury-box-form-floor')}
              margin="dense"
              name="floor"
              type="number"
              value={formik.values.floor}
              error={formik.touched.floor && Boolean(formik.errors.floor)}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[1-9]*',
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">{t('luxury-box-form-floor-number')}</InputAdornment>,
              }}
              onChange={formik.handleChange('floor')}
              onKeyDown={(e) => {
                e.preventDefault();
                return false;
              }}
              disabled={formik.isSubmitting}
            />
          </div>
          <div className="floor-field">
            <TextField
              margin="dense"
              fullWidth
              id="identifierOnFloor"
              name="identifierOnFloor"
              label={t('luxury-box-form-identifierOnFloor')}
              value={formik.values.identifierOnFloor}
              onChange={formik.handleChange}
              error={formik.touched.identifierOnFloor && Boolean(formik.errors.identifierOnFloor)}
              helperText={formik.touched.identifierOnFloor && formik.errors.identifierOnFloor}
              disabled={formik.isSubmitting}
            />
          </div>
        </div>
        <>
          <FormControlLabel
            id="usesCheckingAccount"
            name="usesCheckingAccount"
            control={<Switch checked={formik.values.usesCheckingAccount} onChange={formik.handleChange} />}
            label={t('luxury-box-form-usesCheckingAccount')}
            disabled={formik.isSubmitting}
          />
        </>
        {selectedVenue && selectedVenue.commerces && selectedVenue.commerces.length > 0 && (
          <SelectField
            name="commerces"
            label={t('venue-form-commerces-label')}
            placeholder={t('venue-form-commerces-placeholder')}
            value={formik.values.commerces}
            options={commercesForSelect(selectedVenue.commerces)}
            isMulti={true}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.commerces}
            error={formik.errors.commerces}
            isClearable={true}
            backspaceRemovesValue={true}
            isDisabled={formik.isSubmitting}
            isSearchable={true}
          />
        )}
        <div className="footer">
          <Button className="btn-50" variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
            {`${luxuryBox ? t('luxury-box-form-edit') : t('listLuxuryBoxes-page-create')} `}
          </Button>
        </div>
      </form>
    </>
  );
};
